import { memo } from 'react'
import { Zap, BarChart3, Code2, SlidersHorizontal, Layers, ShieldCheck, LucideIcon } from 'lucide-react'
import { AnimatedSection } from '../animations/AnimatedSection'

interface Feature {
  id: string
  icon: LucideIcon
  title: string
  description: string
  benefit: string
  comingSoon?: boolean
}

const SMART_FEATURES: Feature[] = [
  {
    id: 'simple-api',
    icon: Code2,
    title: 'One API, All Models',
    description: 'Stop managing multiple provider SDKs, keys, and billing accounts. One integration gives you access to every major AI model.',
    benefit: 'Available now in beta'
  },
  {
    id: 'predictable-pricing',
    icon: ShieldCheck,
    title: 'Predictable Credit Pricing',
    description: 'No more surprise invoices. Buy credits upfront and know exactly what each request costs before you make it.',
    benefit: 'No hidden fees, ever'
  },
  {
    id: 'auto-model',
    icon: Zap,
    title: 'Smart Model Routing',
    description: 'Automatically sends each request to the most cost-effective model capable of handling it - same quality, lower spend.',
    benefit: 'Cuts costs without sacrificing quality',
    comingSoon: true
  },
  {
    id: 'caching',
    icon: Layers,
    title: 'Response Caching',
    description: 'Identical or near-identical requests return cached results instantly. No redundant API calls, no wasted tokens.',
    benefit: 'Eliminate redundant spend',
    comingSoon: true
  },
  {
    id: 'prompt-mgmt',
    icon: SlidersHorizontal,
    title: 'Prompt Optimization',
    description: 'Automatically compresses and optimizes prompts to use fewer tokens without changing the output quality.',
    benefit: 'Fewer tokens, same results',
    comingSoon: true
  },
  {
    id: 'analytics',
    icon: BarChart3,
    title: 'Usage Analytics',
    description: 'Track spend, usage, and performance in real time. Know exactly where your AI budget is going.',
    benefit: 'Full visibility into your AI costs'
  },
]

export const SmartFeaturesSection = memo(function SmartFeaturesSection() {
  return (
    <section id="features" className="section-spacing px-4 sm:px-6" aria-labelledby="features-heading">
      <div className="container-custom">
        <AnimatedSection animation="fade-up">
          <div className="text-center mb-8 sm:mb-12">
            <h2 id="features-heading" className="text-3xl sm:text-4xl font-bold text-text mb-3 sm:mb-4">
              The cost optimization layer{' '}
              <span className="text-gradient">your AI stack is missing</span>
            </h2>
            <p className="text-base sm:text-lg text-text-muted max-w-2xl mx-auto px-4">
              We're building every optimization technique that reduces AI spend - zero config, zero extra work on your end. You call one API, we handle the rest.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {SMART_FEATURES.map((feature, index) => {
            const Icon = feature.icon
            const staggerDelay = index * 100
            return (
              <AnimatedSection key={feature.id} animation="fade-up" delay={staggerDelay}>
                <article className="card-enhanced p-5 sm:p-6 flex flex-col gap-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#7C8CFF]/10 flex items-center justify-center" aria-hidden="true">
                      <Icon className="text-[#7C8CFF]" size={20} />
                    </div>
                    {feature.comingSoon && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-accent/10 text-accent border border-accent/20 whitespace-nowrap">
                        Coming soon
                      </span>
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-text mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm sm:text-base text-text-muted mb-3">
                      {feature.description}
                    </p>
                    <p className="text-xs sm:text-sm text-[#7C8CFF] font-medium">
                      {feature.benefit}
                    </p>
                  </div>
                </article>
              </AnimatedSection>
            )
          })}
        </div>

        <AnimatedSection animation="fade-up" delay={600}>
          <div className="mt-10 sm:mt-14 text-center max-w-2xl mx-auto px-4">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse"></span>
              <span className="text-xs font-semibold uppercase tracking-widest text-accent/70">Always improving</span>
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse"></span>
            </div>
            <p className="text-sm sm:text-base text-text-muted">
              AI cost optimization is a fast-moving space. We track every new technique - from speculative decoding to semantic caching - and ship them directly into the platform. Your plan gets more powerful over time without you changing a line of code.
            </p>
          </div>
        </AnimatedSection>
      </div>
    </section>
  )
})
